import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarDays, CheckCircle2, ClipboardList, Phone, PhoneCall } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Motion";
import { Stripes } from "../components/Bits";
import { CalendlyEmbed, TrustMicrocopy } from "../components/Calendly";
import { Field, SubmitButton, useLeadForm } from "../components/Forms";
import { readScope } from "../lib/api";
import { BUSINESS } from "../data/site";

const WINDOWS = ["Morning · 8–11am", "Midday · 11am–2pm", "Afternoon · 2–5pm", "Evening · 5–7pm"];
const PROJECT_TYPES = ["Interior", "Exterior", "Cabinets", "Condo / multi-unit", "Commercial", "Not sure yet"];
const VISIT = [
  "Ilias walks every room or surface in scope with you.",
  "He checks walls, trim and ceilings for repairs, stains and prep needs.",
  "You talk through colours, sheens and low-VOC options in your own light.",
  "He measures and notes the finish tier for each area.",
  "You agree on timing — start date and finish date.",
  "Your written, itemized quote arrives within 24 hours.",
];

const StepRail = ({ step }) => (
  <ol className="grid grid-cols-3 gap-2" data-testid="booking-step-rail">
    {["Choose a time", "Confirm details", "You're booked"].map((l, i) => (
      <li key={l} className={`border-t-2 pt-3 text-xs font-semibold sm:text-sm ${step >= i + 1 ? "border-flame text-white" : "border-white/15 text-slate-400"}`}>
        <span className="font-mono">0{i + 1}</span> <span className="ml-1">{l}</span>
      </li>
    ))}
  </ol>
);

const today = () => new Date().toISOString().slice(0, 10);

const ChooseTime = ({ mode, setMode, when, setWhen, onBooked, next }) => (
  <div data-testid="booking-step-1">
    <div className="inline-flex rounded-full border border-slate-300 bg-white p-1" role="tablist">
      {[["calendly", "Pick a live time", CalendarDays], ["request", "Request a time", ClipboardList]].map(([id, label, Icon]) => (
        <button key={id} type="button" role="tab" aria-selected={mode === id} onClick={() => setMode(id)} data-testid={`booking-mode-${id}`}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-colors ${mode === id ? "bg-navy text-white" : "text-navy hover:bg-slate-100"}`}>
          <Icon className="h-4 w-4" /> {label}
        </button>
      ))}
    </div>
    <div className="mt-6">
      {mode === "calendly" ? (
        <>
          <CalendlyEmbed onScheduled={onBooked} height={680} />
          <button type="button" onClick={next} className="mt-4 text-sm font-semibold text-slate-700 underline decoration-flame decoration-2 underline-offset-4" data-testid="booking-calendly-continue-btn">I've picked my time — continue</button>
        </>
      ) : (
        <div className="rounded-md border border-slate-200 bg-white p-6 sm:p-8" data-testid="booking-fallback-form">
          <Field label="Preferred date" id="b-date">
            <input id="b-date" type="date" min={today()} className="field" value={when.date} onChange={(e) => setWhen((w) => ({ ...w, date: e.target.value }))} data-testid="booking-date-input" />
          </Field>
          <p className="field-label mt-6">Preferred time window</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {WINDOWS.map((w) => (
              <button key={w} type="button" onClick={() => setWhen((s) => ({ ...s, window: w }))} data-testid={`booking-window-${w.split(" ")[0].toLowerCase()}`}
                className={`rounded-md border px-4 py-3 text-left text-sm font-semibold transition-colors ${when.window === w ? "border-flame bg-flame/10 text-navy" : "border-slate-200 text-navy hover:border-navy/40"}`}>{w}</button>
            ))}
          </div>
          <button type="button" disabled={!when.date || !when.window} onClick={next} className="btn-primary mt-8 disabled:cursor-not-allowed disabled:opacity-40" data-testid="booking-step-1-next-btn">
            Continue <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
    <div className="mt-5"><TrustMicrocopy /></div>
  </div>
);

const ConfirmDetails = ({ type, setType, mode, when, booked, scope, back, done }) => {
  const { v, on, busy, send } = useLeadForm({ name: "", phone: "", email: "", postal_code: "", project_type: scope?.Project || "", message: "" });
  const phone = type === "phone_consult";
  const submit = (e) => {
    e.preventDefault();
    const timing = mode === "request" ? { preferred_date: when.date, preferred_window: when.window } : {};
    send({ type, ...v, ...timing, scope: scope || undefined, calendly_booked: booked }, done);
  };
  return (
    <form onSubmit={submit} data-testid="booking-step-2">
      <div className="rounded-md border border-slate-200 bg-white p-6 sm:p-8">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Full name" id="b-name"><input id="b-name" required className="field" value={v.name} onChange={on("name")} data-testid="booking-name-input" /></Field>
          <Field label="Mobile phone" id="b-phone"><input id="b-phone" type="tel" required className="field" value={v.phone} onChange={on("phone")} data-testid="booking-phone-input" /></Field>
          <Field label="Email" id="b-email"><input id="b-email" type="email" required className="field" value={v.email} onChange={on("email")} data-testid="booking-email-input" /></Field>
          <Field label="Postal code" id="b-postal"><input id="b-postal" required maxLength={7} className="field uppercase" value={v.postal_code} onChange={on("postal_code")} placeholder="M6P 1A1" data-testid="booking-postal-input" /></Field>
          <div className="sm:col-span-2">
            <Field label="Project type" id="b-type">
              <select id="b-type" required className="field" value={v.project_type} onChange={on("project_type")} data-testid="booking-project-type-select">
                <option value="">Select one</option>
                {PROJECT_TYPES.map((p) => <option key={p}>{p}</option>)}
              </select>
            </Field>
          </div>
          <div className="sm:col-span-2"><Field label="Anything Ilias should know? (optional)" id="b-msg"><textarea id="b-msg" rows={3} className="field" value={v.message} onChange={on("message")} data-testid="booking-message-input" /></Field></div>
        </div>
        {scope && <p className="mt-5 rounded-md bg-paper p-4 text-sm text-slate-700" data-testid="booking-scope-summary"><span className="font-bold text-navy">Your scope: </span>{Object.entries(scope).filter(([k]) => k !== "booked").map(([k, x]) => `${k}: ${x}`).join(" · ")}</p>}
      </div>
      <button type="button" onClick={() => setType(phone ? "assessment" : "phone_consult")} data-testid="booking-phone-consult-toggle"
        className={`mt-4 flex w-full items-start gap-4 rounded-md border p-5 text-left transition-colors ${phone ? "border-flame bg-flame/10" : "border-slate-200 bg-white hover:border-navy/40"}`}>
        <PhoneCall className="mt-0.5 h-5 w-5 shrink-0 text-flame-600" />
        <span>
          <span className="block font-display text-lg font-bold text-navy">{phone ? "Phone consult selected — switch back to an in-person visit" : "Prefer to talk first? Book a free 15–30 min phone consult instead."}</span>
          <span className="mt-1 block text-sm text-slate-600">{phone ? "Ilias will call you to talk through the project before any visit." : "Ilias calls you, answers your questions, and books the visit when you're ready."}</span>
        </span>
      </button>
      <div className="mt-6 flex flex-col-reverse gap-4 sm:flex-row sm:items-center sm:justify-between">
        <button type="button" onClick={back} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-navy" data-testid="booking-back-btn"><ArrowLeft className="h-4 w-4" /> Back</button>
        <SubmitButton busy={busy} testId="booking-submit-btn">{phone ? "Book my phone consult" : "Confirm my free visit"}</SubmitButton>
      </div>
      <div className="mt-5"><TrustMicrocopy /></div>
    </form>
  );
};

const Confirmation = ({ type, mode }) => (
  <div className="rounded-md border border-slate-200 bg-white p-7 sm:p-10" data-testid="booking-confirmation">
    <CheckCircle2 className="h-12 w-12 text-flame-600" />
    {type === "phone_consult" ? (
      <>
        <h2 className="mt-5 text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">You're booked — Ilias will call you.</h2>
        <p className="mt-3 text-slate-600">Your free 15–30 minute phone consult request is in. Ilias will call the number you gave us.</p>
      </>
    ) : (
      <>
        <h2 className="mt-5 text-3xl font-extrabold tracking-tight text-navy sm:text-4xl" data-testid="booking-confirmation-title">You're booked — Ilias will text you before arrival.</h2>
        {mode === "request" && <p className="mt-3 text-slate-600">Ilias will text you to confirm your requested time.</p>}
        <p className="eyebrow mt-8 text-flame-600">What happens in your 30-minute visit</p>
        <ul className="mt-4 space-y-3">
          {VISIT.map((x) => <li key={x} className="flex gap-3 text-navy"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-flame-600" />{x}</li>)}
        </ul>
      </>
    )}
    <div className="mt-8 flex flex-wrap gap-3">
      <Link to="/" className="btn-ghost-light" data-testid="booking-home-btn">Back to home</Link>
      <a href={BUSINESS.phoneHref} className="btn-primary" data-testid="booking-confirm-call-btn"><Phone className="h-4 w-4" /> {BUSINESS.phone}</a>
    </div>
  </div>
);

export default function BookPage() {
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState("calendly");
  const [type, setType] = useState("assessment");
  const [when, setWhen] = useState({ date: "", window: "" });
  const [booked, setBooked] = useState(false);
  const [scope] = useState(() => readScope());
  const onBooked = useCallback(() => { setBooked(true); setStep(2); }, []);

  return (
    <>
      <Seo title="Book a Free Painting Assessment | The Amora Colour & Scope Visit™" description="Book your free 30-minute Colour & Scope Visit with Ilias. Written quote within 24 hours. Free to book, reschedule or cancel anytime. Toronto, GTA and cottage country." />
      <section className="relative bg-navy grain pb-16 pt-14 sm:pt-20" data-testid="book-page">
        <div className="container-x relative">
          <Stripes className="h-3.5 w-10" animate />
          <h1 className="mt-6 max-w-4xl text-[2.5rem] font-extrabold leading-[0.95] tracking-tight text-white sm:text-6xl">The Amora Colour & Scope Visit<sup className="align-super text-[0.35em] text-flame">™</sup></h1>
          <p className="mt-5 max-w-2xl text-base text-slate-300 md:text-lg">Free. 30 minutes. In person with Ilias. A written quote within 24 hours — no pressure, no obligation.</p>
          <div className="mt-10 max-w-3xl"><StepRail step={step} /></div>
        </div>
      </section>
      <section className="bg-paper py-12 sm:py-16">
        <div className="container-x grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <motion.div key={step} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
              {step === 1 && <ChooseTime mode={mode} setMode={setMode} when={when} setWhen={setWhen} onBooked={onBooked} next={() => setStep(2)} />}
              {step === 2 && <ConfirmDetails type={type} setType={setType} mode={mode} when={when} booked={booked} scope={scope} back={() => setStep(1)} done={() => setStep(3)} />}
              {step === 3 && <Confirmation type={type} mode={mode} />}
            </motion.div>
          </div>
          <aside className="lg:col-span-4">
            <Reveal className="rounded-md bg-navy p-7 text-white lg:sticky lg:top-28">
              <p className="eyebrow text-flame">Your visit includes</p>
              <ul className="mt-5 space-y-3 text-sm text-slate-200">
                {["30 minutes on site with Ilias", "Colour & sheen guidance in your light", "Prep and repair assessment", "Written, itemized quote within 24 hours"].map((x) => <li key={x} className="flex gap-3"><CheckCircle2 className="h-4 w-4 shrink-0 text-flame" />{x}</li>)}
              </ul>
              <div className="mt-6 border-t border-white/10 pt-6"><TrustMicrocopy dark /></div>
              <a href={BUSINESS.phoneHref} className="btn-ghost-dark mt-6 w-full" data-testid="book-aside-call-btn"><Phone className="h-4 w-4" /> Call {BUSINESS.phone}</a>
            </Reveal>
          </aside>
        </div>
      </section>
    </>
  );
}
